"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

interface HeaderProps {
  onLoginClick?: () => void;
  onSignupClick?: () => void;
  transparent?: boolean;
}

export default function Header({ onLoginClick, onSignupClick }: HeaderProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const router = useRouter();
  const { user, signOut, loading } = useAuth();
  const { t, lang, setLang } = useLanguage();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    try {
      setDropdownOpen(false);
      await signOut();
      router.push("/homepage");
    } catch (err) {
      console.error("Sign out error:", err);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.includes("#")) {
      const [, hash] = href.split("#");
      if (pathname === "/homepage") {
        e.preventDefault();
        scrollToSection(hash);
        setMobileOpen(false);
      }
    }
  };

  const isLinkActive = (href: string): boolean => {
    if (href.includes("#")) return false;
    return pathname === href;
  };

  const mainNavLinks = [
    { href: "/homepage", label: t.navHome },
    { href: "/machines", label: t.navMachines },
    { href: "/pricing", label: t.navSubscription },
    { href: "/homepage#om-oss", label: t.navAbout },
    { href: "/homepage#kontakt", label: t.navContact },
  ];

  const dropdownLinks = [
    { href: "/dashboard", label: t.navDashboard },
    { href: "/lagrede-maskiner", label: lang === 'no' ? 'Lagrede maskiner' : 'Saved machines' },
    { href: "/faktura", label: t.navInvoice },
    { href: "/innstillinger", label: t.navSettings },
  ];

  const displayName =
    user?.user_metadata?.full_name?.split(" ")[0] ||
    user?.email?.split("@")[0] ||
    "Bruker";

  const navLinkColor = (href: string) =>
    isLinkActive(href) ? "#ffffff" : "rgba(255,255,255,0.45)";

  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        backgroundColor: "#0A0A0A",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        height: "52px",
      }}
    >
      <div
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "0 24px",
          height: "52px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
        }}
      >
        {/* ── LEFT: Logo ── */}
        <Link
          href="/homepage"
          style={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700,
            fontSize: "18px",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            color: "white",
            textDecoration: "none",
            flexShrink: 0,
            lineHeight: 1,
          }}
        >
          SEBNORGE
        </Link>

        {/* ── CENTER: Nav links — absolutely centered ── */}
        <nav
          className="hidden md:flex"
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            alignItems: "center",
            gap: "36px",
          }}
        >
          {mainNavLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              style={{
                fontFamily: "'Nunito Sans', sans-serif",
                fontWeight: 300,
                fontSize: "11px",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: navLinkColor(link.href),
                textDecoration: "none",
                whiteSpace: "nowrap",
                transition: "color 0.2s ease",
                lineHeight: 1,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = "white";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = navLinkColor(link.href);
              }}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* ── RIGHT: Buttons + Flag ── */}
        <div
          className="hidden md:flex"
          style={{
            alignItems: "center",
            gap: "16px",
            flexShrink: 0,
          }}
        >
          {isMounted && !loading && (
            user ? (
              /* Logged in: name + dropdown + flag */
              <div
                ref={dropdownRef}
                style={{ position: "relative", display: "flex", alignItems: "center", gap: "16px" }}
              >
                {/* User name + chevron */}
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="btn-press btn-press-dark"
                  style={{
                    fontFamily: "'Nunito Sans', sans-serif",
                    fontWeight: 300,
                    fontSize: "11px",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "white",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "0",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayName}
                  <svg
                    width="8"
                    height="5"
                    viewBox="0 0 8 5"
                    fill="none"
                    style={{
                      transition: "transform 0.2s ease",
                      transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                      opacity: 0.6,
                    }}
                  >
                    <path d="M1 1L4 4L7 1" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                {/* Dropdown */}
                {dropdownOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 12px)",
                      right: 0,
                      backgroundColor: "#111111",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: "0",
                      minWidth: "160px",
                      zIndex: 100,
                    }}
                  >
                    {dropdownLinks.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        onClick={() => setDropdownOpen(false)}
                        style={{
                          display: "block",
                          fontFamily: "'Nunito Sans', sans-serif",
                          fontWeight: 300,
                          fontSize: "11px",
                          letterSpacing: "0.08em",
                          color: "rgba(255,255,255,0.7)",
                          textDecoration: "none",
                          padding: "10px 16px",
                          transition: "color 0.15s ease, background 0.15s ease",
                          whiteSpace: "nowrap",
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLAnchorElement).style.color = "white";
                          (e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.04)";
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLAnchorElement).style.color = "rgba(255,255,255,0.7)";
                          (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                        }}
                      >
                        {link.label}
                      </Link>
                    ))}
                    <div style={{ height: "1px", backgroundColor: "rgba(255,255,255,0.08)", margin: "4px 0" }} />
                    <button
                      onClick={handleSignOut}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        fontFamily: "'Nunito Sans', sans-serif",
                        fontWeight: 300,
                        fontSize: "11px",
                        letterSpacing: "0.08em",
                        color: "rgba(255,255,255,0.7)",
                        background: "transparent",
                        border: "none",
                        padding: "10px 16px",
                        cursor: "pointer",
                        transition: "color 0.15s ease, background 0.15s ease",
                        whiteSpace: "nowrap",
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.color = "white";
                        (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.04)";
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,0.7)";
                        (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                      }}
                    >
                      {t.navLogout}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Not logged in: Login + Signup */
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <button
                  className="btn-press btn-press-dark"
                  onClick={onLoginClick}
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontWeight: 600,
                    fontSize: "10px",
                    letterSpacing: "0.25em",
                    textTransform: "uppercase",
                    background: "transparent",
                    color: "white",
                    border: "1px solid white",
                    borderRadius: "0",
                    padding: "7px 18px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    transition: "opacity 0.2s ease",
                    lineHeight: 1,
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = "0.75"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = "1"; }}
                >
                  {t.navLogin}
                </button>

                <button
                  className="btn-press"
                  onClick={onSignupClick}
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontWeight: 600,
                    fontSize: "10px",
                    letterSpacing: "0.25em",
                    textTransform: "uppercase",
                    background: "white",
                    color: "#000000",
                    border: "1px solid white",
                    borderRadius: "0",
                    padding: "7px 18px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    transition: "background 0.2s ease",
                    lineHeight: 1,
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#E0E0E0"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "white"; }}
                >
                  {t.navSignup}
                </button>
              </div>
            )
          )}

          {/* Flag toggle — always visible, 20px gap from last button handled by parent gap:16px */}
          {isMounted && (
            <button
              onClick={() => setLang(lang === "no" ? "en" : "no")}
              title={lang === "no" ? "Switch to English" : "Bytt til norsk"}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "20px",
                lineHeight: 1,
                padding: "0",
                opacity: 0.7,
                transition: "opacity 0.2s ease",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = "1"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = "0.7"; }}
            >
              {lang === "no" ? "🇬🇧" : "🇳🇴"}
            </button>
          )}
        </div>

        {/* ── MOBILE: Hamburger + Flag ── */}
        <div className="md:hidden flex items-center" style={{ gap: "16px" }}>
          {isMounted && (
            <button
              onClick={() => setLang(lang === "no" ? "en" : "no")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "20px",
                lineHeight: 1,
                padding: "0",
                opacity: 0.7,
                transition: "opacity 0.2s ease",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = "1"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = "0.7"; }}
            >
              {lang === "no" ? "🇬🇧" : "🇳🇴"}
            </button>
          )}
          <button
            className="md:hidden btn-press btn-press-dark"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              gap: "5px",
              padding: "4px",
            }}
          >
            <span
              style={{
                display: "block",
                width: "22px",
                height: "1px",
                backgroundColor: "white",
                transition: "all 0.3s ease",
                transform: mobileOpen ? "rotate(45deg) translate(4px, 4px)" : "none",
              }}
            />
            <span
              style={{
                display: "block",
                width: "22px",
                height: "1px",
                backgroundColor: "white",
                transition: "all 0.3s ease",
                opacity: mobileOpen ? 0 : 1,
              }}
            />
            <span
              style={{
                display: "block",
                width: "22px",
                height: "1px",
                backgroundColor: "white",
                transition: "all 0.3s ease",
                transform: mobileOpen ? "rotate(-45deg) translate(4px, -4px)" : "none",
              }}
            />
          </button>
        </div>
      </div>

      {/* ── MOBILE MENU ── */}
      {mobileOpen && (
        <div
          className="md:hidden"
          style={{
            backgroundColor: "#0A0A0A",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            padding: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          {mainNavLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              onClick={(e) => { handleNavClick(e, link.href); setMobileOpen(false); }}
              style={{
                fontFamily: "'Nunito Sans', sans-serif",
                fontWeight: 300,
                fontSize: "11px",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: isLinkActive(link.href) ? "white" : "rgba(255,255,255,0.45)",
                textDecoration: "none",
              }}
            >
              {link.label}
            </Link>
          ))}

          {isMounted && user && (
            <>
              <div style={{ height: "1px", backgroundColor: "rgba(255,255,255,0.06)" }} />
              {dropdownLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  style={{
                    fontFamily: "'Nunito Sans', sans-serif",
                    fontWeight: 300,
                    fontSize: "11px",
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.45)",
                    textDecoration: "none",
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </>
          )}

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              paddingTop: "16px",
              borderTop: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            {isMounted && user ? (
              <button
                onClick={() => { handleSignOut(); setMobileOpen(false); }}
                style={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontWeight: 600,
                  fontSize: "10px",
                  letterSpacing: "0.25em",
                  textTransform: "uppercase",
                  background: "white",
                  color: "#000000",
                  border: "1px solid white",
                  borderRadius: "0",
                  padding: "10px 18px",
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                {t.navLogout}
              </button>
            ) : (
              <>
                <button
                  onClick={() => { onLoginClick?.(); setMobileOpen(false); }}
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontWeight: 600,
                    fontSize: "10px",
                    letterSpacing: "0.25em",
                    textTransform: "uppercase",
                    background: "transparent",
                    color: "white",
                    border: "1px solid white",
                    borderRadius: "0",
                    padding: "10px 18px",
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  {t.navLogin}
                </button>
                <button
                  onClick={() => { onSignupClick?.(); setMobileOpen(false); }}
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontWeight: 600,
                    fontSize: "10px",
                    letterSpacing: "0.25em",
                    textTransform: "uppercase",
                    background: "white",
                    color: "#000000",
                    border: "1px solid white",
                    borderRadius: "0",
                    padding: "10px 18px",
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  {t.navSignup}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}